<template>
  <v-card class="pa-5 mt-12 rounded-xxl">
    <v-card-title>
      <h2 class="font-weight-medium">
        {{ t("components.auth.confirm.title") }}
      </h2>
    </v-card-title>
    <v-card-text class="mt-5">
      {{ t("components.auth.confirm.description") }}
    </v-card-text>
    <v-card-text>
      <v-form @submit.prevent="confirmUser">
        <y-text-field
          v-model="confirmationCode"
          :label="t('components.auth.confirm.codeLabel')"
          type="number"
          class="no-inline-number-controls"
          required
          autocapitalize="none"
          autocomplete="off"
          :error-messages="
            (v$.confirmationCode.$dirty &&
              v$.confirmationCode.$invalid &&
              v$.confirmationCode.$silentErrors[0].$message.toString()) ||
            ''
          "
          @input="authStore.resetErrorMessages"
        />
        <y-btn
          :loading="authStore.confirmUser.status === LOADING_STATES.LOADING"
          :disabled="isConfirmButtonDisabled()"
          class="mt-5"
          block
          type="submit"
        >
          {{ t("components.auth.confirm.confirmButton") }}
        </y-btn>
        <v-alert
          v-if="authStore.confirmUser.errorMessage"
          density="compact"
          outlined
          type="warning"
          class="mt-4"
        >
          {{ authStore.confirmUser.errorMessage }}
        </v-alert>
      </v-form>
    </v-card-text>
    <v-card-text>
      <y-btn-nd :disabled="isResendButtonDisabled()" block @click.prevent="resendConfirmationCode">
        {{ t("components.auth.confirm.resendButton") }}
      </y-btn-nd>
      <p class="text-left mt-5">
        <a href="/" @click.prevent="exit">{{ t("form.back") }}</a>
      </p>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore, LOADING_STATES } from "@/store/authentication";

const { t } = useI18n();

const { email } = defineProps<{ email: string }>();
const emit = defineEmits<{
  confirmationSuccess: [];
  confirmationFailure: [];
  resendSuccess: [];
  exit: [];
}>();
const confirmationCode = ref("");

const authStore = useAuthenticationStore();

const v$ = useVuelidate(
  {
    confirmationCode: {
      $autoDirty: true,
      required: helpers.withMessage(t("form.required"), required),
    },
  },
  { confirmationCode },
);

const isConfirmButtonDisabled = () =>
  v$.value.$invalid || authStore.confirmUser.status === LOADING_STATES.LOADING;

const isResendButtonDisabled = () => authStore.confirmUser.status === LOADING_STATES.LOADING;

const confirmUser = async () => {
  const emailLowercase = email.toLocaleLowerCase();
  const confirmUserSuccess = await authStore.initConfirmUser({
    email: emailLowercase,
    confirmationCode: confirmationCode.value,
  });
  if (confirmUserSuccess) {
    emit("confirmationSuccess");
    return;
  }
  emit("confirmationFailure");
};

const resendConfirmationCode = async () => {
  const emailLowercase = email.toLocaleLowerCase();
  const resendSuccess = await authStore.initResendConfirmationCode(emailLowercase);
  if (resendSuccess) {
    emit("resendSuccess");
    return;
  }
};

const exit = () => emit("exit");
</script>
